import React, { useEffect, useRef, useState } from 'react';
import Isotope from 'isotope-layout';
import StackIcon from 'tech-stack-icons';
import acfIcon from '../assets/acf.svg';
import composerIcon from '../assets/Composer.svg';
import doctrineIcon from '../assets/Doctrine.svg';
import learndashIcon from '../assets/learndash.svg';
import mailchimpIcon from '../assets/mailchimp.svg';
import restIcon from '../assets/rest.svg';
import woocommerceIcon from '../assets/WooCommerce.svg';
import wpmlIcon from '../assets/wpml.svg';
import gutenbergIcon from '../assets/gutenberg.svg';
import bamboraIcon from '../assets/bambora.svg';
import mysqlIcon from '../assets/mysql.svg';
import sqlIcon from '../assets/Sql.svg';



const filters = [
  { label: 'All', value: 'all' },
  { label: 'Languages', value: 'languages' },
  { label: 'Frameworks & Libraries', value: 'frameworks' },
  { label: 'WordPress', value: 'wordpress' },
  { label: 'Databases & APIs', value: 'databases' },
  { label: 'Cloud & DevOps', value: 'cloud' },
  { label: 'Development Tools', value: 'tools' },
];

const techStack = [
  { name: 'PHP', icon: 'php', category: 'languages' },
  { name: 'JavaScript', icon: 'js', category: 'languages' },
  { name: 'HTML5', icon: 'html5', category: 'languages' },
  { name: 'CSS3', icon: 'css3', category: 'languages' },
  { name: 'SQL', icon: 'csql', category: 'languages' },

  { name: 'jQuery', icon: 'jquery', category: 'frameworks' },
  { name: 'React', icon: 'react', category: 'frameworks' },
  { name: 'Vue.js', icon: 'vuejs', category: 'frameworks' },
  { name: 'CodeIgniter', icon: 'codeigniter', category: 'frameworks' },
  { name: 'Laravel', icon: 'laravel', category: 'frameworks' },
  { name: 'Symfony', icon: 'symfony', category: 'frameworks' },
  { name: 'Doctrine', icon: 'doctrine', category: 'frameworks' },

  { name: 'WordPress', icon: 'wordpress', category: 'wordpress' },
  { name: 'Gutenberg', icon: 'gutenberg', category: 'wordpress' },
  { name: 'ACF', icon: 'acf', category: 'wordpress' },
  { name: 'WPML', icon: 'wpml', category: 'wordpress' },
  { name: 'LearnDash', icon: 'learndash', category: 'wordpress' },
  //{ name: 'Custom Post Types', icon: 'wordpress', category: 'wordpress' },
  { name: 'WooCommerce', icon: 'woocommerce', category: 'wordpress' },

  { name: 'MySQL', icon: 'cmysql', category: 'databases' },
  { name: 'MariaDB', icon: 'mariadb', category: 'databases' },
    { name: 'Redis', icon: 'redis', category: 'databases' },
  { name: 'REST APIs', icon: 'rest', category: 'databases' },
  { name: 'Mailchimp API', icon: 'mailchimp', category: 'databases' },

  { name: 'Bambora API', icon: 'bambora', category: 'databases' },


  { name: 'Linux', icon: 'linux', category: 'cloud' },
  { name: 'Google Cloud', icon: 'gcloud', category: 'cloud' },
  { name: 'AWS', icon: 'aws', category: 'cloud' },
  { name: 'Git', icon: 'git', category: 'cloud' },
  { name: 'Apache', icon: 'apache', category: 'cloud' },
  { name: 'Nginx', icon: 'nginx', category: 'cloud' },

  { name: 'Composer', icon: 'composer', category: 'tools' },
  { name: 'Gulp', icon: 'gulp', category: 'tools' },
  { name: 'Grunt', icon: 'gruntjs', category: 'tools' },
  { name: 'Webpack', icon: 'webpack', category: 'tools' },
  { name: 'Yarn', icon: 'yarn', category: 'tools' },
  { name: 'NPM', icon: 'npm', category: 'tools' }
];

const customIconMap = {
  acf: acfIcon,
  doctrine: doctrineIcon,
  composer: composerIcon,
  rest: restIcon,
  mailchimp: mailchimpIcon,
  woocommerce: woocommerceIcon,
  wpml: wpmlIcon,
  learndash: learndashIcon,
  gutenberg: gutenbergIcon,
  bambora: bamboraIcon,
  cmysql: mysqlIcon,
  csql: sqlIcon,
};

const TechStack = () => {
  const gridRef = useRef(null);
  const isotopeRef = useRef(null);
  const [activeFilter, setActiveFilter] = useState('languages');

  useEffect(() => {
    if (!gridRef.current) return;

    isotopeRef.current = new Isotope(gridRef.current, {
      itemSelector: '.tech-item',
      layoutMode: 'fitRows',
      percentPosition: true,
      transitionDuration: '0.35s',
    });

    return () => {
      isotopeRef.current?.destroy();
    };
  }, []);

  useEffect(() => {
    if (!isotopeRef.current) return;

    isotopeRef.current.arrange({
      filter: activeFilter === 'all' ? '*' : `.${activeFilter}`,
    });
  }, [activeFilter]);

  return (
    <section id="tech-stack" name="tech-stack" className="w-full bg-[#3d3a4b] py-20 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mt-[60px] mb-[100px] text-center">
          <h2 className="mt-4 text-3xl font-black sm:text-4xl md:text-5xl">My Tech Stack</h2>
        </div>

        <div className="mb-8 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
          {filters.map((filter) => (
            <button
              key={filter.value}
              type="button"
              onClick={() => setActiveFilter(filter.value)}
              className={`rounded-full border px-4 py-2 text-[10px] font-bold uppercase tracking-[0.18em] transition-all duration-200 sm:text-xs ${
                activeFilter === filter.value
                  ? 'border-[#fe3c3a] bg-[#fe3c3a] text-white shadow-lg shadow-[#fe3c3a]-500/25'
                  : 'border-white/15 bg-white/5 text-white/80 hover:border-[#fe3c3a] hover:text-white'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div ref={gridRef} className="flex flex-wrap justify-center gap-4 sm:gap-5">
          {techStack.map((item) => (
            <div
              key={`${item.category}-${item.name}`}
              className={`tech-item ${item.category}  min-w-[170px]  sm:w-[calc(33%-0.7rem)] lg:w-[calc(15%-0.75rem)] p-4`}
            >
    
                <div className="group flex h-full min-h-[150px] flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-4 text-center shadow-lg shadow-black/20 transition duration-300  hover:border-[#fe3c3a]">
                  {customIconMap[item.icon] ? (
                    <img
                      src={customIconMap[item.icon]}
                      alt={item.name}
                      className="h-12 w-12 object-contain sm:h-14 sm:w-14"
                    />
                  ) : (
                    <StackIcon name={item.icon} variant="dark" className="h-12 w-12 sm:h-14 sm:w-14" />
                  )}
                  <span className="mt-3 block text-[11px] font-semibold uppercase tracking-[0.12em] text-white/90 sm:text-xs">
                    {item.name}
                  </span>
                </div>
             
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default TechStack;